// Estado de conectividade reativo, com verificação ativa.
//
// navigator.onLine sozinho não é fiável dentro do WebView2 (o motor usado
// pelo Tauri no Windows) — é um problema conhecido de motores embutidos
// baseados em Chromium não dispararem 'online'/'offline' de forma
// consistente quando a ligação real volta, deixando a app a acreditar que
// continua offline muito depois da internet já ter voltado (o utilizador
// reportou exatamente isto — sobretudo no desktop). Por isso, além de
// escutar os eventos do browser, este hook também confirma via um pedido
// real e leve à própria API (só enquanto se acredita estar offline) — mais
// fiável do que confiar apenas na propriedade do browser.
import { useEffect, useRef, useState } from "react";
import { apiGetPushConfig } from "./authApi";

const ACTIVE_PROBE_INTERVAL_MS = 15000;

function readBrowserOnlineState() {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(readBrowserOnlineState);
  const probeInFlightRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Verificação ativa: só corre enquanto se acredita estar offline (nunca
  // desperdiça pedidos quando o estado já está bem) — usa
  // apiGetPushConfig() por ser público (sem sessão) e já resolver a
  // baseURL certa em qualquer ambiente (web/produção/desktop Tauri,
  // incluindo o fallback entre DESKTOP_API_BASE_URLS já existente em
  // authApi.js), em vez de assumir um caminho relativo que só funciona
  // quando a app e a API partilham a mesma origem.
  useEffect(() => {
    if (isOnline) return undefined;
    if (typeof window === "undefined") return undefined;

    let active = true;

    const probe = async () => {
      if (probeInFlightRef.current) return;
      probeInFlightRef.current = true;
      try {
        await apiGetPushConfig();
        if (active) setIsOnline(true);
      } catch {
        // continua offline — nada a fazer, tenta de novo no próximo ciclo.
      } finally {
        probeInFlightRef.current = false;
      }
    };

    void probe();
    const intervalId = window.setInterval(probe, ACTIVE_PROBE_INTERVAL_MS);

    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [isOnline]);

  return isOnline;
}
